import React, { ReactNode } from 'react';
import { Text } from '@sitecore-jss/sitecore-jss-react';
import { compose } from 'ramda';

import { withEditMode, withEditModeNotice } from '_containers/BaseComponent';
import { Download as DownloadIcon } from '_utils/icons';
import { useTranslation } from 'react-i18next';
import { ListOfFileDownloadsProps } from './definitions';
import {
  DownloadWrapper,
  IconWrapper,
  TextWrapper,
  DownloadLink,
  DownloadTitle,
  DownloadAttributes,
  DownloadIconWrapper,
  DownloadDescription,
  VariationWrapper,
  VariationDownloadWrapper,
  EditorDiv
} from './StyledListOfFileDownloads';

import { getIcon, filterDownloads, renderDate, renderSize } from './helpers';

const EditorText = compose(withEditModeNotice)(EditorDiv);

const renderDownloadItem = (
  {
    id,
    title: { jss: title },
    file: { jss: file },
    lastUpdated: { date: lastUpdated },
    variations,
    description
  },
  editMode,
  translations
): ReactNode => {
  let isVariation = true;
  let variationsToShow = [];
  let hasFileAttached = true;
  let extension;
  let size;
  let src;
  let mimeType;

  try {
    extension = file.value.extension;
    size = file.value.size;
    mimeType = file.value.mimeType;

    src = file.value.src;

    if (!editMode) {
      // Add `download-true` for IE support
      src = new URL(file.value.src);

      if (!src.searchParams.has('download')) {
        src.searchParams.set('download', 'true');
      }

      src = `${src.origin}${src.pathname}?${src.searchParams.toString()}`;
    }
  } catch {
    hasFileAttached = false;
  }

  if (typeof variations !== 'undefined') {
    isVariation = false;
    variationsToShow = variations.filter(filterDownloads.bind(null, editMode));
  }

  return (
    <React.Fragment key={id}>
      <IconWrapper $variation={isVariation.toString()}>{hasFileAttached && getIcon(extension)}</IconWrapper>
      <TextWrapper $variation={isVariation.toString()}>
        <EditorText noticeEnabled={!hasFileAttached} noticeMessage={`No file has been associated`}>
          <DownloadLink href={src} type={mimeType} download>
            <DownloadTitle>
              <Text field={title} />
            </DownloadTitle>
            {!isVariation && hasFileAttached && (
              <DownloadIconWrapper>
                <DownloadIcon />
              </DownloadIconWrapper>
            )}
          </DownloadLink>
          {hasFileAttached && (
            <DownloadAttributes>
              {extension.toUpperCase()} · {renderSize(size)} · {translations.lastUpdated}{' '}
              {renderDate(lastUpdated)}
            </DownloadAttributes>
          )}
          {!isVariation && (description?.jss?.value || editMode) && (
            <DownloadDescription>
              <Text field={description.jss} />
            </DownloadDescription>
          )}
        </EditorText>
        {variationsToShow.length > 0 && (
          <VariationWrapper>
            {variationsToShow.map((c) => (
              <VariationDownloadWrapper key={c.id}>
                {renderDownloadItem(c, editMode, translations)}
              </VariationDownloadWrapper>
            ))}
          </VariationWrapper>
        )}
      </TextWrapper>
    </React.Fragment>
  );
};

const DownloadableFiles: React.FC<ListOfFileDownloadsProps> = ({
  fields: {
    data: { datasource }
  },
  editMode
}) => {
  const [t] = useTranslation();
  const translations = {
    lastUpdated: t('downloadable-files-last-updated')
  };

  if (datasource === null) {
    if (editMode) {
      return (
        <EditorText noticeEnabled noticeMessage="No datasource associated to this component" />
      );
    }

    return null;
  }

  return (
    <DownloadWrapper key={datasource.id}>
      {renderDownloadItem(datasource, editMode, translations)}
    </DownloadWrapper>
  );
};

export default compose(withEditMode)(DownloadableFiles);
